import type { ServiceStatus } from '@/lib/schemas/db'

import { CALLOUT_STATUS_ORDER } from './calloutStatus'
import { JOB_STATUS_ORDER } from './jobStatus'
import { SESSION_STATUS_ORDER } from './sessionStatus'

/**
 * Every status across every service type, in one workflow-shaped order.
 *
 * Hand-arranged rather than concatenated, because the three service types run
 * parallel workflows that share their ends: all of them start New-ish, stall on
 * Blocked, and finish Invoiced or Cancelled. Concatenating the three orders
 * would repeat those shared phases and read as noise in a dropdown. This reads
 * top to bottom as: intake → scheduled → work started → work ended → stalled →
 * wrapping up → invoicing → closed.
 */
const PREFERRED_ORDER: ServiceStatus[] = [
  'New',
  'NewJob',
  'Booked',
  'ReadyToWork',
  'Arrived',
  'Started',
  'InProgress',
  'LeftSite',
  'Ended',
  'Blocked',
  'WaitingOnParts',
  'Review',
  'ReadyForCollection',
  'ReadyForCollectionUnsuccessful',
  'ReadyToInvoice',
  'Invoiced',
  'Cancelled',
]

/**
 * Every status a service can hold, deduped, in PREFERRED_ORDER.
 *
 * Anything a service type declares but PREFERRED_ORDER forgot is appended
 * rather than dropped — a new status shows up in the filters immediately, just
 * in the wrong place, instead of silently going missing. serviceStatusOrder.test.ts
 * fails on that so the position gets fixed, but a slip degrades to "ugly", never
 * to "invisible".
 */
export const ALL_SERVICE_STATUSES: ServiceStatus[] = [
  ...PREFERRED_ORDER,
  ...[...CALLOUT_STATUS_ORDER, ...SESSION_STATUS_ORDER, ...JOB_STATUS_ORDER].filter(
    (status) => !PREFERRED_ORDER.includes(status),
  ),
].filter((status, index, all) => all.indexOf(status) === index)

/** Statuses an archived service can hold — the ledger is built from these two. */
export const ARCHIVED_SERVICE_STATUSES: ServiceStatus[] = ['Invoiced', 'Cancelled']
